import React from 'react';
import { 
  Drawer, 
  Box, 
  IconButton, 
  Button, 
  FormControlLabel, 
  Typography 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import CustomSwitch from '../CustomSwitch';
import { categoryColors, areaColors, scheduleFilterButtonColors } from '../../utils/colors';
import { fetchUserAttributes, fetchAuthSession } from 'aws-amplify/auth'; // 追加: fetchAuthSession を使用

const FilterSidebar = ({ isMobile, calendarState, onSignOut, user, isDesk }) => {
  const [cognitoName, setCognitoName] = React.useState('');
  // 初期値は props の isDesk を考慮しつつ、既定は「ユーザー」
  const [groupLabel, setGroupLabel] = React.useState(isDesk ? 'デスク' : 'ユーザー');

  React.useEffect(() => {
    let mounted = true;

    // グループ名配列を日本語ラベルにマッピング（優先度: Admins > Desks > Reporters > users）
    const mapGroupsToLabel = (groups) => {
      const arr = Array.isArray(groups) ? groups : (typeof groups === 'string' ? [groups] : []);
      const lower = arr.map(g => String(g).toLowerCase());
      if (lower.some(g => g === 'admins' || g.includes('admin'))) return '管理者';
      if (lower.some(g => g === 'desks' || g === 'desk' || g.includes('desk'))) return 'デスク';
      if (lower.some(g => g === 'reporters' || g === 'reporter' || g.includes('reporter'))) return '記者';
      if (lower.some(g => g === 'users' || g === 'user')) return 'ユーザー';
      return 'ユーザー';
    };

    (async () => {
      try {
        // ユーザー属性（表示名）
        const attrs = await fetchUserAttributes().catch(() => null);
        if (mounted && attrs) {
          const display =
            attrs?.name ||
            ([attrs?.family_name, attrs?.given_name].filter(Boolean).join(' ') || '') ||
            attrs?.preferred_username ||
            '';
          setCognitoName(display);
        }
      } catch (e) {
        console.warn('ユーザー属性取得失敗', e);
      }

      // グループ（cognito:groups）を ID トークンから取得
      try {
        const session = await fetchAuthSession();
        // Amplify v6 の形: session.tokens.idToken.payload['cognito:groups']
        const groups =
          session?.tokens?.idToken?.payload?.['cognito:groups'] ??
          session?.idToken?.payload?.['cognito:groups'] ??
          [];
        if (mounted) {
          setGroupLabel(mapGroupsToLabel(groups));
        }
      } catch (e) {
        // 取得できない場合は props の isDesk をフォールバック
        if (mounted) setGroupLabel(isDesk ? 'デスク' : 'ユーザー');
        console.warn('グループ情報取得失敗', e);
      }
    })();

    return () => { mounted = false; };
  }, [isDesk]);

  return (
    <Drawer
      anchor="right"
      open={!isMobile || calendarState.isDrawerOpen}
      onClose={calendarState.toggleDrawer(false)}
      variant={!isMobile ? 'persistent' : 'temporary'}
      sx={{
        '& .MuiDrawer-paper': {
          width: 300,
          bgcolor: '#f5f5f5',
          boxShadow: 3,
          p: 2,
          position: !isMobile ? 'relative' : 'fixed',
        },
      }}
    >
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 0,
            mb: 2,
          }}
        >
          <IconButton 
            onClick={calendarState.toggleDrawer(false)} 
            aria-label="close" 
            sx={{ right: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* ユーザーステータス（グループ表記） */}
      <Box sx={{ mt: 1, mb: 2, textAlign: 'center', width: '100%' }}>
        <Box
          sx={{
            display: 'inline-block',
            px: 4,
            py: 1,
            bgcolor: '#e0e0e0',
            borderRadius: 2,
            border: '1px solid #bdbdbd',
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px',
          }}
        >
          <Typography variant="body2" component="div">
            <Box sx={{mt: 1, mb: 1, textAlign: 'left'}}>
              ユーザー：{cognitoName ? `${cognitoName}` : ''}
            </Box>
            <Box sx={{mt: 1, mb: 1, textAlign: 'left'}}>
              グループ：{groupLabel}
            </Box>
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: isMobile ? 1 : 3,
          mb: isMobile ? 1 : 3,
      }}
      >
        <FormControlLabel
          control={
            <CustomSwitch
              checked={calendarState.autoUpdate}
              onChange={(e) => calendarState.setAutoUpdate(e.target.checked)}
            />
          }
          label="自動更新"
          sx={{
            gap: 1.2,
            '& .MuiFormControlLabel-label': {
              fontSize: '1.0rem',
              fontWeight: 'bold',
              color: calendarState.autoUpdate ? '#000000' : '#a9a9a9',
              transition: 'color 0.5s ease-in-out',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          mt: 3,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {Object.keys(areaColors).map((area) => (
            <Button
              key={area}
              variant={calendarState.selectedAreas.includes(area) ? 'contained' : 'outlined'}
              color={calendarState.selectedAreas.includes(area) ? 'primary' : 'default'}
              onClick={() => {
                if (calendarState.selectedAreas.includes(area)) {
                  calendarState.setSelectedAreas(calendarState.selectedAreas.filter((a) => a !== area));
                } else {
                  calendarState.setSelectedAreas([...calendarState.selectedAreas, area]);
                }
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                backgroundColor: calendarState.selectedAreas.includes(area)
                  ? scheduleFilterButtonColors.background
                  : scheduleFilterButtonColors.selectedBackground,
                color: calendarState.selectedAreas.includes(area)
                  ? scheduleFilterButtonColors.text
                  : scheduleFilterButtonColors.selectedText,
                borderColor: scheduleFilterButtonColors.border,
                borderWidth: '1px',
                borderStyle: 'solid',
                transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  backgroundColor: scheduleFilterButtonColors.hoverBackground,
                  borderColor: scheduleFilterButtonColors.hoverBorder,
                },
              }}
            >
              {area}
            </Button>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
          {Object.keys(categoryColors).map((category) => (
            <Button
              key={category}
              variant={calendarState.selectedCategories.includes(category) ? 'contained' : 'outlined'}
              color={calendarState.selectedCategories.includes(category) ? 'primary' : 'default'}
              onClick={() => {
                if (calendarState.selectedCategories.includes(category)) {
                  calendarState.setSelectedCategories(calendarState.selectedCategories.filter((c) => c !== category));
                } else {
                  calendarState.setSelectedCategories([...calendarState.selectedCategories, category]);
                }
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 'bold',
                backgroundColor: calendarState.selectedCategories.includes(category)
                  ? scheduleFilterButtonColors.background
                  : scheduleFilterButtonColors.selectedBackground,
                color: calendarState.selectedCategories.includes(category)
                  ? scheduleFilterButtonColors.text
                  : scheduleFilterButtonColors.selectedText,
                borderColor: scheduleFilterButtonColors.border,
                borderWidth: '1px',
                borderStyle: 'solid',
                transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
                '&:hover': {
                  backgroundColor: scheduleFilterButtonColors.hoverBackground,
                  borderColor: scheduleFilterButtonColors.hoverBorder,
                },
              }}
            >
              {category}
            </Button>
          ))}
        </Box>

        <Box
          sx={{ 
            display: 'flex',
            gap: 1,
            mt: 3,
            mb: 3,
            justifyContent: 'center',
            width: '100%'
          }
        }>
          <Button
            variant="outlined"
            onClick={() => calendarState.setSelectedCategories(Object.keys(categoryColors))}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: scheduleFilterButtonColors.background,
              color: scheduleFilterButtonColors.text,
              borderColor: scheduleFilterButtonColors.border,
              transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
              '&:hover': {
                backgroundColor: scheduleFilterButtonColors.hoverBackground,
                color: scheduleFilterButtonColors.text,
                borderColor: scheduleFilterButtonColors.hoverBorder,
              },
            }}
          >
            全て選択
          </Button>
          <Button
            variant="outlined"
            onClick={() => calendarState.setSelectedCategories([])}
            sx={{
              textTransform: 'none',
              fontWeight: 'bold',
              backgroundColor: scheduleFilterButtonColors.background,
              color: scheduleFilterButtonColors.text,
              borderColor: scheduleFilterButtonColors.border,
              transition: 'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
              '&:hover': {
                backgroundColor: scheduleFilterButtonColors.hoverBackground,
                color: scheduleFilterButtonColors.text,
                borderColor: scheduleFilterButtonColors.hoverBorder,
              },
            }}
          >
            全て解除
          </Button>
        </Box>

        <Box sx={{ mt: '45px', textAlign: 'center', width: '100%' }}>
          <Button
            component="a"
            href="http://192.168.63.133"
            variant="contained"
            size="small"
            sx={{
              width: '100%',
              bgcolor: '#1976d2',
              color: '#fff',
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              '&:hover': { bgcolor: '#1565c0' },
              mb: 2,
              py: 1,
            }}
          >
            アーカイブ
          </Button>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              if (window.confirm('ログアウトしますか？')) {
                onSignOut();
              }
            }}
            size="small"
            sx={{
              display: 'inline-block',
              bgcolor: '#212121',
              color: '#fff',
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              '&:hover': { bgcolor: '#424242' },
              mt: 2,
              mb: 2,
              px: 3,
              py: 1,
            }}
          >
            ログアウト
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterSidebar;